import TaMoAkiLogo from '@/components/branding/TaMoAkiLogo';
import OptimizedImage from '@/components/ui/OptimizedImage';
import { useAuth } from '@/hooks/useAuth';
import { useUserProfile } from '@/hooks/useUserProfile';
import {
    Activity,
    Cog,
    LifeBuoy,
    LogOut,
    MapPin,
    MessageSquare,
    Search,
    User,
    UserCircle,
    Users,
    X
} from 'lucide-react';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface SidebarProps {
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { userProfile } = useUserProfile();

  const navigationItems = [
    {
      id: 'explore',
      label: 'Explorar Serviços',
      icon: Search,
      path: '/categorias',
      description: 'Buscar e filtrar serviços',
      color: 'blue'
    },
    {
      id: 'prestadores',
      label: 'Prestadores',
      icon: Users,
      path: '/prestadores',
      description: 'Ver todos os profissionais da plataforma',
      color: 'violet'
    },
    {
      id: 'nearby-search',
      label: 'Na sua zona',
      icon: MapPin,
      path: '/nearby-search',
      description: 'Prestadores que atendem onde precisa',
      color: 'emerald'
    },
    { 
      id: 'messages', 
      label: 'Mensagens', 
      icon: MessageSquare, 
      path: '/messages',
      description: 'Chat com prestadores',
      color: 'purple'
    },
    { 
      id: 'atividade', 
      label: 'Atividade', 
      icon: Activity, 
      path: '/atividade',
      description: 'Agendamentos, Favoritos e Avaliações',
      color: 'orange'
    },
  ];

  const profileItems = [
    { 
      id: 'profile', 
      label: 'Perfil', 
      icon: UserCircle, 
      path: '/profile',
      description: 'Dados pessoais e preferências',
      color: 'indigo'
    },
    { 
      id: 'settings', 
      label: 'Configurações', 
      icon: Cog, 
      path: '/settings',
      description: 'Notificações e privacidade',
      color: 'slate'
    },
    { 
      id: 'support', 
      label: 'Suporte', 
      icon: LifeBuoy, 
      path: '/ajuda',
      description: 'Ajuda e contato',
      color: 'rose'
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleNavigation = (path: string) => {
    navigate(path);
    if (onClose) {
      onClose();
    }
  };

  const handleSignOut = async () => {
    console.log('[Sidebar] Clique no botão Sair');
    try {
      // Navegar para a página principal ANTES de limpar a sessão. Se
      // formos chamados a partir de uma rota protegida (ex: /dashboard),
      // limpar a sessão primeiro faz o ProtectedRoute (ainda montado)
      // reagir a user=null e forçar <Navigate to="/login">, que ganhava
      // a corrida ao nosso navigate('/') — daí continuar a acabar em
      // /login mesmo depois de mudarmos o destino aqui.
      navigate('/');
      await signOut();
      console.log('[Sidebar] signOut executado com sucesso');
      toast.success('Sessão encerrada com sucesso');
      console.log('[Sidebar] Redirecionamento para a página principal');
    } catch (error) {
      console.error('[Sidebar] Erro ao encerrar sessão:', error);
      toast.error('Erro ao encerrar sessão');
    }
  };

  const getColorClasses = (color: string, isActive: boolean) => {
    const colorMap = {
      blue: {
        active: 'bg-blue-50 text-blue-700 border-blue-200',
        icon: isActive ? 'text-blue-600' : 'text-blue-400',
        indicator: 'bg-blue-500'
      },
      emerald: {
        active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        icon: isActive ? 'text-emerald-600' : 'text-emerald-400',
        indicator: 'bg-emerald-500'
      },
      purple: {
        active: 'bg-purple-50 text-purple-700 border-purple-200',
        icon: isActive ? 'text-purple-600' : 'text-purple-400',
        indicator: 'bg-purple-500'
      },
      orange: {
        active: 'bg-orange-50 text-orange-700 border-orange-200',
        icon: isActive ? 'text-orange-600' : 'text-orange-400',
        indicator: 'bg-orange-500'
      },
      indigo: {
        active: 'bg-indigo-50 text-indigo-700 border-indigo-200',
        icon: isActive ? 'text-indigo-600' : 'text-indigo-400',
        indicator: 'bg-indigo-500'
      },
      slate: {
        active: 'bg-slate-50 text-slate-700 border-slate-200',
        icon: isActive ? 'text-slate-600' : 'text-slate-400',
        indicator: 'bg-slate-500'
      },
      rose: {
        active: 'bg-rose-50 text-rose-700 border-rose-200',
        icon: isActive ? 'text-rose-600' : 'text-rose-400',
        indicator: 'bg-rose-500'
      }
    };
    return colorMap[color] || colorMap.slate;
  };

  return (
    <div className="w-72 bg-gradient-to-b from-slate-50 to-white shadow-2xl flex flex-col h-screen relative overflow-hidden border-r border-slate-200/50">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/10 to-indigo-200/10 rounded-full blur-2xl" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-200/10 to-pink-200/10 rounded-full blur-xl" />
      
      {/* Header modernizado */}
      <div className="flex items-center justify-between p-5 border-b border-slate-200/50 relative z-10">
        <div className="flex items-center space-x-4">
          <div className="transform hover:scale-105 transition-transform duration-300">
            <TaMoAkiLogo size="md" showText={false} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Inter',system-ui,-apple-system,sans-serif]">
              TaMoAki
            </h1>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"></div>
              <span className="text-sm text-slate-600 font-['Inter',system-ui,-apple-system,sans-serif]">
                {userProfile?.is_prestador ? 'Prestador' : 'Cliente'}
              </span>
            </div>
          </div>
        </div>
        
        {onClose && (
          <button
            onClick={onClose}
            className="p-3 rounded-2xl hover:bg-slate-100 transition-all duration-300 transform hover:scale-105 group"
            aria-label="Fechar menu"
          >
            <X className="w-5 h-5 text-slate-500 group-hover:text-slate-700" />
          </button>
        )}
      </div>

      {/* User Info modernizado */}
      <div className="p-5 border-b border-slate-200/50 relative z-10">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-gradient-to-br from-orange-400 to-orange-500 shadow-lg overflow-hidden border-2 border-white">
            {userProfile?.avatar_url ? (
              <OptimizedImage 
                src={userProfile.avatar_url} 
                alt="Avatar" 
                className="w-12 h-12 object-cover rounded-2xl" 
                width={48}
                height={48}
                responsive
                responsiveSizes={[48, 72]}
              />
            ) : (
              <User className="w-6 h-6 text-white" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-slate-900 truncate font-['Inter',system-ui,-apple-system,sans-serif]">
              {user?.user_metadata?.full_name || 'Usuário'}
            </p>
            <p className="text-xs text-slate-500 truncate font-['Inter',system-ui,-apple-system,sans-serif]">
              {user?.email}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation modernizada */}
      <nav className="flex-1 min-h-0 overflow-y-auto relative z-10">
        <div className="p-5">
          <div className="space-y-5">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full"></div>
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider font-['Inter',system-ui,-apple-system,sans-serif]">
                  Navegação
                </h3>
              </div>
              
              <div className="space-y-2">
                {navigationItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  const colors = getColorClasses(item.color, active);
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNavigation(item.path)}
                      className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-2xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 group relative ${
                        active 
                          ? `${colors.active} border shadow-lg` 
                          : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                      title={item.description}
                    >
                      {/* Indicador visual ativo */}
                      {active && (
                        <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 ${colors.indicator} rounded-r-full`}></div>
                      )}
                      
                      <div className={`p-2 rounded-xl transition-all duration-300 ${
                        active 
                          ? 'bg-white/80 shadow-sm' 
                          : 'group-hover:bg-slate-100'
                      }`}>
                        <Icon className={`w-5 h-5 ${colors.icon} transition-colors duration-300`} />
                      </div>
                      <span className="flex-1 text-left font-['Inter',system-ui,-apple-system,sans-serif]">
                        {item.label}
                      </span>
                      
                      {/* Indicador de hover */}
                      {!active && (
                        <div className="w-2 h-2 bg-slate-300 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-1 h-6 bg-gradient-to-b from-purple-500 to-pink-500 rounded-full"></div>
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider font-['Inter',system-ui,-apple-system,sans-serif]">
                  Conta
                </h3>
              </div>
              
              <div className="space-y-2">
                {profileItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  const colors = getColorClasses(item.color, active);
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNavigation(item.path)}
                      className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-2xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 group relative ${
                        active 
                          ? `${colors.active} border shadow-lg` 
                          : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                      title={item.description}
                    >
                      {/* Indicador visual ativo */}
                      {active && (
                        <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 ${colors.indicator} rounded-r-full`}></div>
                      )}
                      
                      <div className={`p-2 rounded-xl transition-all duration-300 ${
                        active 
                          ? 'bg-white/80 shadow-sm' 
                          : 'group-hover:bg-slate-100'
                      }`}>
                        <Icon className={`w-5 h-5 ${colors.icon} transition-colors duration-300`} />
                      </div>
                      <span className="flex-1 text-left font-['Inter',system-ui,-apple-system,sans-serif]">
                        {item.label}
                      </span>
                      
                      {/* Indicador de hover */}
                      {!active && (
                        <div className="w-2 h-2 bg-slate-300 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Footer modernizado */}
      <div className="p-5 border-t border-slate-200/50 relative z-10 space-y-2">
        {/* Theme Toggle ocultado: o dark mode ainda não está completo (a maioria
            das páginas hardcoda cores claras). Reativar quando migrado para
            tokens semânticos. */}

        {/* Logout Button */}
        <button
          onClick={handleSignOut}
          className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-2xl text-sm font-semibold text-slate-700 hover:bg-red-50 hover:text-red-700 transition-all duration-300 transform hover:scale-105 group"
        >
          <div className="p-2 rounded-xl group-hover:bg-red-100 transition-all duration-300">
            <LogOut className="w-5 h-5 text-slate-400 group-hover:text-red-500 transition-colors duration-300" />
          </div>
          <span className="flex-1 text-left font-['Inter',system-ui,-apple-system,sans-serif]">
            Sair
          </span>
          <div className="w-2 h-2 bg-slate-300 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        </button>
      </div>
    </div>
  );
};

export default Sidebar; 